import CallIcon from '@mui/icons-material/Call';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import { Divider, IconButton, Paper, PaperProps, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DataEntry } from '../../../components/DataEntry';
import { ExpandedBox } from '../../../components/ExpandedBox';
import { FullSizeLoader } from '../../../components/FullSizeLoader';
import { useClipboard } from '../../../hooks/useClipboard';

type Props = {
  title: string;
  contact?: {
    name?: string | null;
    email?: string | null;
    phoneNumber?: string | null;
  } | null;
  isLoading?: boolean;
  isError?: boolean;
} & PaperProps;

export const ContactInfoBox = ({ contact, title, isLoading, isError, sx, ...rest }: Props) => {
  const { t } = useTranslation();
  const { copyToClipboard } = useClipboard();
  return (
    <Paper sx={{ p: { md: 4, xs: 2 }, position: 'relative', minHeight: 270, ...sx }} {...rest}>
      {isLoading ? <FullSizeLoader /> : null}
      {isError ? (
        <ExpandedBox sx={{ backdropFilter: 'blur(2px)', zIndex: 10 }}>
          <Typography>{t('query.error.default')}</Typography>
        </ExpandedBox>
      ) : null}
      <Typography variant={'h2'}>{title}</Typography>
      <Typography variant={'subtitle1'}>{t('contactInfo')}</Typography>
      <Stack sx={{ mt: 4 }} gap={2}>
        <DataEntry title={t('firstName')} text={contact?.name ?? ''} />
        <Stack direction={'row'} flexWrap={'wrap'} gap={2}>
          <DataEntry
            flex={1}
            title={t('email')}
            text={
              <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={2}>
                <Typography>{contact?.email}</Typography>
                {contact ? (
                  <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={2}>
                    <IconButton color={'primary'} component={Link} to={`mailto:${contact.email}`} target={'_blank'}>
                      <ForwardToInboxIcon />
                    </IconButton>
                    <IconButton color={'primary'} onClick={() => copyToClipboard(contact.email ?? '')}>
                      <ContentCopyIcon />
                    </IconButton>
                  </Stack>
                ) : null}
              </Stack>
            }
          />
          <DataEntry
            flex={1}
            title={t('phone')}
            text={
              <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={2}>
                <Typography>{contact?.phoneNumber}</Typography>
                {contact ? (
                  <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={2}>
                    <IconButton color={'primary'} component={Link} to={`tel:${contact?.phoneNumber}`} target={'_blank'}>
                      <CallIcon />
                    </IconButton>
                    <IconButton color={'primary'} onClick={() => copyToClipboard(contact?.phoneNumber ?? '')}>
                      <ContentCopyIcon />
                    </IconButton>
                  </Stack>
                ) : null}
              </Stack>
            }
          />
        </Stack>
      </Stack>
    </Paper>
  );
};
